import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import { FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { AuthHeader } from '../../components/auth/AuthHeader'
import { AuthOtpStep } from '../../components/auth/AuthOtpStep'
import { AuthSendCodeStep } from '../../components/auth/AuthSendCodeStep'
import {
  PhoneOtpDeliveryRecovery,
  type PhoneOtpEmailCta,
} from '../../components/auth/PhoneOtpDeliveryRecovery'
import { BackButton } from '../../components/ui/BackButton'
import { Button } from '../../components/ui/Button'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { PhoneInput } from '../../components/ui/PhoneInput'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { productTelemetryService } from '../../lib/services/product-telemetry.service'
import {
  classifyPhoneOtpDeliveryError,
  isInvalidPhoneNumberError,
  isPhoneInUseError,
  isPhoneOtpDeliveryError,
  phoneOtpRetryAfterSeconds,
  type PhoneOtpDeliveryReason,
  isPhoneChangeNeedsCurrentError,
} from '../../lib/errors/service-error'
import { stagePhoneForLogin } from '../../lib/auth/phone-handoff'
import { openPhoneOtpSupport } from '../../lib/auth/open-phone-otp-support'
import {
  canDeliverOtp,
  classifyOtpIneligibility,
  otpIneligibilityMessageKey,
} from '../../lib/utils/otp-delivery'
import { hasVerifiedAuthEmail, realContactEmail } from '../../lib/utils/account-contact'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'
import { env } from '../../lib/env'
import { SPACING } from '../../lib/brand'
import { useSemanticColors, DURATION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'

/** What failed, and which action was in flight when it did. */
type DeliveryIssue = {
  reason: PhoneOtpDeliveryReason
  mode: 'send' | 'resend'
}

/**
 * A destination is not a live OTP challenge until the server confirms the
 * send. Keeping this separate from `pendingPhone` matters because a staged
 * phone arrives before the automatic request starts.
 */
type OtpChallengeState = 'idle' | 'sending' | 'ready' | 'failed'

/**
 * How long the banner's retry stays locked, per cause. The screen used to
 * offer an instant retry after a refused send, which walked the user straight
 * back into the same refusal — a button that looks like a way out and is not.
 *
 * These are floors. When the backend states a wait ("…after 47 seconds"),
 * `phoneOtpRetryAfterSeconds` wins, because a number we were given beats a
 * number we guessed.
 */
const DELIVERY_RETRY_COOLDOWN_SECONDS: Record<PhoneOtpDeliveryReason, number> = {
  // Project-wide bucket; it refills continuously, so a minute usually clears.
  service_rate_limited: 60,
  // Our own budget is 10 sends per hour per user (request-phone-link). Keep a
  // short floor so a 429 does not lock the button for 5 minutes after we clear
  // the server block — ops already raised the budget for outage recovery.
  account_rate_limited: 60,
  // An outage can clear at any moment, and this branch hands the user a human
  // instead of a timer, so the lock only needs to stop reflex double-taps.
  provider_rejected: 30,
}

/**
 * The phone Supabase already holds for this auth user.
 *
 * `user.phone` is set once Auth has confirmed the number; before that, the
 * value typed at signup lives in `user_metadata.phone`, which is where the
 * email+phone registration path puts it. Both are read because this screen sits
 * exactly between the two states — it is the screen that moves a number from
 * the second to the first.
 */
function pendingAuthUserPhone(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null
  const record = user as { phone?: unknown; user_metadata?: unknown }

  // Only the PENDING number, never the confirmed one.
  //
  // A first version of this read `user.phone` first — the number Auth has
  // already confirmed — and that broke changing your phone entirely: this
  // screen auto-sends an OTP to whatever it is seeded with and never renders
  // the input when it has a number, so somebody arriving from Perfil to switch
  // numbers got a code sent to the number they were trying to leave, with no
  // field to type the new one into. A confirmed phone is precisely the case
  // where this screen must start empty.
  // If Auth has ANY confirmed phone, this screen is a change-phone flow and
  // must start empty.
  //
  // The version before this one seeded from metadata whenever it DIFFERED from
  // the confirmed number, which is precisely backwards: `user_metadata.phone` is
  // whatever was typed at signup and is never updated afterwards, so for anybody
  // who has ever changed their number it holds the OLD one. The screen would
  // have auto-sent a code to the number they left behind, with no field to type
  // the new one into. Differing is the signal that it is stale, not that it is
  // wanted.
  const confirmed = typeof record.phone === 'string' ? record.phone.trim() : ''
  if (confirmed) return null

  const meta = (record.user_metadata ?? {}) as Record<string, unknown>
  const pending = typeof meta.phone === 'string' ? meta.phone.trim() : ''
  return pending || null
}

function confirmedAuthPhone(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null
  const record = user as { phone?: unknown; phone_confirmed_at?: unknown }
  if (!record.phone_confirmed_at || typeof record.phone !== 'string') return null
  // GoTrue may return digits without '+'. Compare and send one E.164 form,
  // separately from the signup metadata used to seed an unlinked phone.
  const digits = record.phone.replace(/\D/g, '')
  return digits ? `+${digits}` : null
}

export default function VerifyPhoneScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // Same cut-over AuthScaffold uses to pick between the centered desktop
  // column and the full-height mobile scroll body. Only mobile gets the
  // flexible footer spacer below.
  const { width } = useBreakpoint()
  const isSplit = width >= 900
  // `phone` is present for email/password registration and absent when the
  // AuthGate sends a Google/Apple/email user here to link their first phone.
  //
  // It arrives through the in-memory auth store, never through router params:
  // expo-router serialises params into the web query string, which would leak
  // the number into browser history, hosting access logs and Sentry's
  // browser-tracing URL capture. Snapshot once on mount; the store is cleared
  // right after so a later visit (profile → change phone) never inherits it.
  // Losing it on refresh is safe here — the screen falls back to asking for a
  // phone, which is exactly the no-phone (social login) path.
  const {
    user,
    sendPhoneVerification,
    verifyPhoneChange,
    sendPhoneVerificationPending,
    verifyPhoneChangePending,
    signOut,
  } = useAuth()

  // The number the person already gave us, wherever it ended up.
  //
  // `pendingAuthPhone` is set by the phone-first signup path and is null for
  // everyone else — so somebody who typed their number into the field labelled
  // "Teléfono" on /register arrived here to an empty box and typed it again.
  // Supabase keeps that value on the auth user, which this screen already has.
  //
  // Snapshotted once with a lazy initialiser, like the store read it replaces:
  // this is the number to VERIFY, and it must not change under the person while
  // an OTP for the previous one is in flight.
  const [phone] = useState(
    () => useAuthStore.getState().pendingAuthPhone ?? pendingAuthUserPhone(user) ?? null,
  )
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const setPendingAuthPhone = useAuthStore((s) => s.setPendingAuthPhone)
  const reducedMotion = useReducedMotion()

  /**
   * El número que la cuenta tiene HOY, solo si está confirmado. Un número sin
   * confirmar no prueba nada, así que para esta pantalla es como no tenerlo:
   * ese caso es un vínculo, no un cambio.
   */
  const confirmedPhone = confirmedAuthPhone(user)

  const [error, setError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [deliveryIssue, setDeliveryIssue] = useState<DeliveryIssue | null>(null)
  const [retryCooldown, setRetryCooldown] = useState(0)
  const [externalResendVersion, setExternalResendVersion] = useState(0)
  const [inputPhone, setInputPhone] = useState(phone ?? '')
  /**
   * The barrier, same as phone-login. False until libphonenumber says the
   * prefix+digits in the field is a number a handset can answer.
   *
   * Seeded from the staged `phone` rather than plain `false`: this screen can
   * open with a number already in hand (RootLayoutApp routes here when the
   * account still owes a phone), and starting at false would grey out the only
   * button on screen for a number that is perfectly valid.
   */
  const [phoneValid, setPhoneValid] = useState(() => canDeliverOtp(phone ?? ''))
  const [phoneCandidate, setPhoneCandidate] = useState(phone ?? '')
  const [pendingPhone, setPendingPhone] = useState<string | null>(phone ?? null)
  // Do not mount OtpInput just because a number is staged. Its local resend
  // clock starts on mount, so doing so would visually promise a code before
  // `sendPhoneVerification` has accepted the request.
  const [otpChallengeState, setOtpChallengeState] = useState<OtpChallengeState>(() =>
    phone ? 'sending' : 'idle',
  )
  const [phoneInUse, setPhoneInUse] = useState<string | null>(null)
  /**
   * CAMBIAR DE NÚMERO SON DOS PRUEBAS, NO UNA.
   *
   * El servidor exige que quien cambia su número haya probado antes el que
   * tiene hoy — si no, una sesión abierta sobre una mesa alcanzaba para
   * quedarse con la cuenta de otro, porque para las cuentas que entran por
   * teléfono el número ES la credencial.
   *
   * Acá vive el número NUEVO mientras se prueba el actual. Se detecta antes de
   * enviar nada: esperar el rechazo del servidor costaría un mensaje al número
   * nuevo que igual se iba a rechazar.
   */
  const [changeTarget, setChangeTarget] = useState<string | null>(null)
  const [switchingAccount, setSwitchingAccount] = useState(false)
  const hasSent = useRef(false)
  const otpRequestInFlight = useRef(false)

  const handlePhoneValidityChange = useCallback((valid: boolean, candidate: string) => {
    setPhoneValid(valid)
    setPhoneCandidate(candidate)
  }, [])

  /** Why the send button is inert. See phone-login.tsx for the reasoning. */
  const phoneHint = useMemo(() => {
    if (phoneValid || phoneError) return null
    const reason = classifyOtpIneligibility(phoneCandidate)
    if (!reason || reason === 'empty' || reason === 'not_a_number') return null
    const key = otpIneligibilityMessageKey(reason)
    return key ? t(key) : null
  }, [phoneValid, phoneError, phoneCandidate, t])
  const recordPhoneLinkEvent = (
    eventName: string,
    elementId: string,
    elementLabel: string,
    extra?: Record<string, unknown>,
  ) => {
    productTelemetryService.recordEvent('form_submit', eventName, {
      routePath: '/verify-phone',
      screenName: 'verify-phone',
      elementId,
      elementType: 'auth_step',
      metadata: {
        ...extra,
        elementLabel,
        phonePresent: Boolean(pendingPhone || inputPhone),
      },
    })
  }

  // Release the signup routing guard only after this route is active. If
  // register.tsx clears it before navigation commits, AuthGate sees an
  // authenticated user still on /register and redirects to tabs.
  useEffect(() => {
    setCompletingSignup(false)
  }, [setCompletingSignup])

  // One-shot handoff: clear the staged number as soon as this screen has
  // snapshotted it, so the PII lives no longer than this step needs it.
  useEffect(() => {
    setPendingAuthPhone(null)
  }, [setPendingAuthPhone])

  // Ticks the banner's retry lock down to zero. Same shape as OtpInput's own
  // countdown, kept here because the banner's retry is a different control
  // with a different budget behind it.
  useEffect(() => {
    if (retryCooldown <= 0) return
    const timer = setInterval(() => setRetryCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(timer)
  }, [retryCooldown])

  /**
   * A refused send. Names the cause, arms the retry lock, and — for a provider
   * rejection only — raises it to Sentry.
   *
   * The Sentry split is the whole point: `captureError` downgrades anything
   * `isExpectedNonFatalOperationalError` recognises to a breadcrumb, and until
   * now that set contained every OTP delivery failure. So when the provider refused,
   * nothing was raised, no one was paged, and the outage read as users failing
   * to type a code. Rate limits stay breadcrumbs (they are expected and the
   * user can wait); provider rejections are ours and are now errors.
   *
   * Returns false when the error is not a delivery failure, so callers fall
   * through to their normal error handling.
   */
  const reportDeliveryIssue = useCallback((err: unknown, mode: 'send' | 'resend'): boolean => {
    const reason = classifyPhoneOtpDeliveryError(err)
    if (!reason) return false

    setDeliveryIssue({ reason, mode })
    setRetryCooldown(phoneOtpRetryAfterSeconds(err) ?? DELIVERY_RETRY_COOLDOWN_SECONDS[reason])

    if (reason === 'provider_rejected') {
      captureError(err, {
        screen: 'verify-phone',
        action: mode,
        otpFailure: reason,
      })
    }
    return true
  }, [])

  const handlePhoneInUse = useCallback((attemptedPhone: string) => {
    setPhoneInUse(attemptedPhone)
    setDeliveryIssue(null)
    setError('')
    setPendingPhone(null)
    setOtpChallengeState('idle')
    hasSent.current = false
  }, [])

  const handleEnterWithLinkedPhone = useCallback(async () => {
    if (!phoneInUse || switchingAccount) return
    setSwitchingAccount(true)
    try {
      stagePhoneForLogin(phoneInUse)
      // LA MUTACIÓN, NO `authService.signOut()` A SECAS.
      //
      // La bandera `userInitiatedSignOut` se escribe DENTRO de la mutación
      // (lib/hooks/use-auth.ts:440). Llamando al servicio directo, la bandera
      // nunca se levanta, el listener clasifica el SIGNED_OUT como inesperado y
      // dispara `setSessionExpired(true)` — así que la persona toca un botón
      // que dice "Entrar con este número" y lo que ve es "Tu sesión expiró.
      // Iniciá sesión de nuevo". Un toque, deslogueado y encima una mentira.
      await signOut()
      router.replace({
        pathname: '/(auth)/phone-login' as never,
        params: { mode: 'login' },
      })
    } catch (err) {
      captureError(err, { screen: 'verify-phone', action: 'phone-in-use-switch' })
      toast.error(errorMessage(err, t('auth.errors.network')))
      setSwitchingAccount(false)
    }
  }, [phoneInUse, router, signOut, switchingAccount, t])

  const handleUseOtherNumber = useCallback(() => {
    setPhoneInUse(null)
    setError('')
    setPendingPhone(null)
    setOtpChallengeState('idle')
    hasSent.current = false
  }, [])

  /**
   * The number itself cannot receive an SMS. Not an outage, not capacity — a
   * different problem with a different fix, so it must never wear the outage
   * banner. Drop back to the entry step with the number still in the field and
   * the reason under it, which is the only place a correctable mistake belongs.
   */
  const handleUnreachableNumber = useCallback(
    (attemptedPhone: string) => {
      setDeliveryIssue(null)
      setRetryCooldown(0)
      setError('')
      setInputPhone(attemptedPhone)
      setPendingPhone(null)
      setOtpChallengeState('idle')
      hasSent.current = false
      setPhoneError(t('auth.validation.phone_unreachable'))
      void notification(NotificationType.Error)
    },
    [t],
  )

  /**
   * One headline per cause. The old screen had one headline for all of them —
   * "El SMS está tardando" — which was false in every branch: nothing was
   * tardando, the send had been refused before it ever reached a carrier.
   */
  const deliveryTitle = (issue: DeliveryIssue | null): string => {
    if (!issue) return t('auth.verify_phone_sms_unavailable_title')
    switch (issue.reason) {
      case 'service_rate_limited':
        return t('auth.verify_phone_sms_busy_title')
      case 'account_rate_limited':
        return t('auth.verify_phone_sms_quota_title')
      case 'provider_rejected':
        return t('auth.verify_phone_sms_provider_title')
      default:
        return t('auth.verify_phone_sms_unavailable_title')
    }
  }

  /**
   * Hand-off to a person. The reason this exists at all: a signup whose only
   * forward path is an SMS that is not coming is a dead end, and a dead end at
   * the phone step is the last screen that user ever sees.
   */
  const handleDeliverySupport = () => {
    impact(ImpactStyle.Light)
    recordPhoneLinkEvent(
      'auth.phone_link_support_opened',
      'verify-phone.delivery-support-button',
      'Contactar soporte',
      { reason: deliveryIssue?.reason },
    )
    void openPhoneOtpSupport(t('auth.verify_phone_sms_support_message'), {
      unavailableMessage: t('auth.verify_phone_sms_support_unavailable'),
      screen: 'verify-phone',
    })
  }

  // Auto-send phone verification when register/social gate already handed us
  // a phone. When Google/Apple users land here with no phone yet, the first
  // step below asks for one before sending the OTP.
  useEffect(() => {
    if (!env.isPhoneAuthEnabled) return
    if (pendingPhone && !hasSent.current && !otpRequestInFlight.current) {
      hasSent.current = true
      otpRequestInFlight.current = true
      setOtpChallengeState('sending')
      productTelemetryService.recordEvent('form_submit', 'auth.phone_link_send_started', {
        routePath: '/verify-phone',
        screenName: 'verify-phone',
        elementId: 'verify-phone.auto-send',
        elementType: 'auth_step',
        metadata: {
          elementLabel: 'Enviar código de teléfono',
          mode: 'auto',
          phonePresent: true,
        },
      })
      sendPhoneVerification(pendingPhone)
        .then(() => {
          setOtpChallengeState('ready')
          productTelemetryService.recordEvent('form_submit', 'auth.phone_link_send_succeeded', {
            routePath: '/verify-phone',
            screenName: 'verify-phone',
            elementId: 'verify-phone.auto-send',
            elementType: 'auth_step',
            metadata: {
              elementLabel: 'Código enviado',
              mode: 'auto',
              phonePresent: true,
            },
          })
        })
        .catch((err) => {
          setOtpChallengeState('failed')
          productTelemetryService.recordEvent(
            'form_submit',
            isPhoneOtpDeliveryError(err)
              ? 'auth.phone_link_send_unavailable'
              : 'auth.phone_link_send_failed',
            {
              routePath: '/verify-phone',
              screenName: 'verify-phone',
              elementId: 'verify-phone.auto-send',
              elementType: 'auth_step',
              metadata: {
                elementLabel: 'No se pudo enviar código',
                mode: 'auto',
                phonePresent: true,
              },
            },
          )
          if (isInvalidPhoneNumberError(err)) {
            handleUnreachableNumber(pendingPhone)
            return
          }
          if (isPhoneInUseError(err)) {
            handlePhoneInUse(pendingPhone)
            return
          }
          if (reportDeliveryIssue(err, 'send')) return
          setError(errorMessage(err, t('auth.errors.otp_send_failed')))
          captureError(err, { screen: 'verify-phone', action: 'auto-send' })
        })
        .finally(() => {
          otpRequestInFlight.current = false
        })
    }
  }, [
    handlePhoneInUse,
    handleUnreachableNumber,
    pendingPhone,
    reportDeliveryIssue,
    sendPhoneVerification,
    t,
  ])

  const handleSendCode = async () => {
    if (otpRequestInFlight.current) return
    impact(ImpactStyle.Light)
    const normalized = inputPhone.trim()
    // Same barrier phone-login uses, from the same numbering plan. The button
    // above is already inert for anything this refuses; reaching here means the
    // two disagreed, and the rule wins. (The rule before libphonenumber was
    // `/^\+\d{7,15}$/`, which accepted "+5031234" and sent a code to a number
    // that cannot exist; the length table that replaced it accepted a
    // Guatemalan range that does not exist either.)
    if (!canDeliverOtp(normalized)) {
      const reason = classifyOtpIneligibility(normalized) ?? 'not_a_number'
      setPhoneValid(false)
      setError('')
      setPhoneError(t(otpIneligibilityMessageKey(reason) ?? 'auth.validation.phone_format'))
      void notification(NotificationType.Error)
      return
    }

    setError('')
    setPhoneError('')
    setDeliveryIssue(null)
    setRetryCooldown(0)
    setOtpChallengeState('sending')
    otpRequestInFlight.current = true
    try {
      recordPhoneLinkEvent(
        'auth.phone_link_send_started',
        'verify-phone.send-button',
        'Enviar código de teléfono',
        { mode: 'manual' },
      )
      // Paso 1 de 2. Si la cuenta ya tiene un número confirmado y éste es otro,
      // primero se prueba el actual. El nuevo queda guardado y todavía no se le
      // manda nada: un mensaje al número nuevo antes de la prueba es un crédito
      // gastado en algo que el servidor iba a rechazar.
      const isChange = Boolean(confirmedPhone && normalized !== confirmedPhone)
      const target = isChange ? confirmedPhone! : normalized
      setChangeTarget(isChange ? normalized : null)

      await sendPhoneVerification(target)
      hasSent.current = true
      setPendingPhone(target)
      setOtpChallengeState('ready')
      recordPhoneLinkEvent(
        'auth.phone_link_send_succeeded',
        'verify-phone.send-button',
        'Código enviado',
        { mode: 'manual' },
      )
      void notification(NotificationType.Success)
    } catch (err) {
      setOtpChallengeState('idle')
      recordPhoneLinkEvent(
        isPhoneOtpDeliveryError(err)
          ? 'auth.phone_link_send_unavailable'
          : 'auth.phone_link_send_failed',
        'verify-phone.send-button',
        'No se pudo enviar código',
        { mode: 'manual' },
      )
      if (isInvalidPhoneNumberError(err)) {
        handleUnreachableNumber(normalized)
        return
      }
      if (isPhoneInUseError(err)) {
        handlePhoneInUse(normalized)
        void notification(NotificationType.Error)
        return
      }
      if (reportDeliveryIssue(err, 'send')) {
        void notification(NotificationType.Error)
        return
      }
      setError(errorMessage(err, t('auth.errors.otp_send_failed')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-phone', action: 'send' })
    } finally {
      otpRequestInFlight.current = false
    }
  }

  const handleComplete = async (code: string) => {
    if (!pendingPhone || otpRequestInFlight.current) throw new Error('OTP request unavailable')
    otpRequestInFlight.current = true
    setError('')
    impact(ImpactStyle.Light)
    let currentProofCompleted = false

    try {
      recordPhoneLinkEvent(
        'auth.phone_link_verify_started',
        'verify-phone.otp-input',
        'Verificar teléfono',
      )
      // Qué se está probando ahora mismo. Si hay un número nuevo esperando y el
      // que estamos verificando es el actual, esto es el paso 1: la prueba de
      // que la cuenta es tuya. Si no, es el vínculo o el cambio final.
      const provingCurrent = Boolean(changeTarget && pendingPhone === confirmedPhone)

      await verifyPhoneChange({
        phone: pendingPhone,
        code,
        intent: changeTarget && !provingCurrent ? 'change' : 'link',
        proveCurrent: provingCurrent,
      })

      if (provingCurrent && changeTarget) {
        currentProofCompleted = true
        // Paso 1 listo. La prueba quedó registrada del lado del servidor y vale
        // diez minutos; ahora sí se le manda el código al número nuevo.
        recordPhoneLinkEvent(
          'auth.phone_link_current_proved',
          'verify-phone.otp-input',
          'Número actual confirmado',
        )
        void notification(NotificationType.Success)
        // Advance the challenge before sending: a failed send must retry the
        // NEW number, not re-verify the current number's already-used code.
        setPendingPhone(changeTarget)
        setOtpChallengeState('sending')
        setError('')
        try {
          await sendPhoneVerification(changeTarget)
          setOtpChallengeState('ready')
        } catch (sendError) {
          setOtpChallengeState('failed')
          if (isPhoneInUseError(sendError)) {
            handlePhoneInUse(changeTarget)
          } else if (!reportDeliveryIssue(sendError, 'send')) {
            setError(errorMessage(sendError, t('auth.errors.otp_send_failed')))
            captureError(sendError, { screen: 'verify-phone', action: 'send-new-number' })
          }
          void notification(NotificationType.Error)
          throw sendError
        }
        return
      }

      recordPhoneLinkEvent(
        'auth.phone_link_verified',
        'verify-phone.otp-input',
        'Teléfono verificado',
      )
      setChangeTarget(null)
      void notification(NotificationType.Success)
      router.replace('/(tabs)/explore' as never)
    } catch (err) {
      // The old-number OTP succeeded; the nested send handler already showed
      // why the new challenge could not be delivered. Do not relabel that
      // delivery failure as a wrong old-number code or erase its retry banner.
      if (currentProofCompleted) throw err
      recordPhoneLinkEvent(
        'auth.phone_link_verify_failed',
        'verify-phone.otp-input',
        'Código incorrecto',
      )
      setDeliveryIssue(null)
      setRetryCooldown(0)
      if (isPhoneInUseError(err)) {
        handlePhoneInUse(pendingPhone)
        void notification(NotificationType.Error)
        throw err
      }
      // El servidor pide la prueba del número actual. No es un código malo: es
      // que faltó el paso 1. Puede pasar si la prueba venció (dura diez
      // minutos) o si el cliente es viejo y no la hizo. Se reinicia el flujo
      // desde el paso 1 en vez de dejar a la persona reintentando un código
      // que nunca va a servir.
      if (isPhoneChangeNeedsCurrentError(err) && confirmedPhone) {
        setChangeTarget(pendingPhone)
        setPendingPhone(confirmedPhone)
        setOtpChallengeState('sending')
        setError('')
        try {
          await sendPhoneVerification(confirmedPhone)
          setOtpChallengeState('ready')
        } catch (sendError) {
          setOtpChallengeState('failed')
          if (!reportDeliveryIssue(sendError, 'send')) {
            setError(errorMessage(sendError, t('auth.errors.otp_send_failed')))
            captureError(sendError, { screen: 'verify-phone', action: 'resend-current-number' })
          }
          void notification(NotificationType.Error)
        }
        throw err
      }
      setError(errorMessage(err, t('auth.errors.otp_invalid')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-phone', action: 'verify' })
      throw err
    } finally {
      otpRequestInFlight.current = false
    }
  }

  const handleResend = async () => {
    if (!pendingPhone || otpRequestInFlight.current) throw new Error('OTP request unavailable')
    otpRequestInFlight.current = true
    setError('')
    impact(ImpactStyle.Light)
    try {
      recordPhoneLinkEvent(
        'auth.phone_link_resend_started',
        'verify-phone.resend-button',
        'Reenviar código',
      )
      await sendPhoneVerification(pendingPhone)
      setDeliveryIssue(null)
      setRetryCooldown(0)
      recordPhoneLinkEvent(
        'auth.phone_link_resend_succeeded',
        'verify-phone.resend-button',
        'Código reenviado',
      )
    } catch (err) {
      recordPhoneLinkEvent(
        isPhoneOtpDeliveryError(err)
          ? 'auth.phone_link_resend_unavailable'
          : 'auth.phone_link_resend_failed',
        'verify-phone.resend-button',
        'No se pudo reenviar código',
      )
      if (isInvalidPhoneNumberError(err)) {
        handleUnreachableNumber(pendingPhone)
        throw err
      }
      if (reportDeliveryIssue(err, 'resend')) {
        // The banner renders under the OTP boxes and can sit below the fold on
        // a short screen — more so at large text — so the toast carries the
        // same headline up to where the user is looking. It says which of the
        // three things happened, not "it is taking a while".
        const reason = classifyPhoneOtpDeliveryError(err)
        toast.error(deliveryTitle(reason ? { reason, mode: 'resend' } : null))
        void notification(NotificationType.Error)
        throw err
      }
      // Surface the real reason via
      // toast (errorMessage resolves err.userMessageKey when present),
      // capture to Sentry so engineers see resend failures.
      toast.error(errorMessage(err, t('auth.errors.otp_resend_failed')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-phone', action: 'resend' })
      throw err
    } finally {
      otpRequestInFlight.current = false
    }
  }

  /**
   * SMS never arrived. Retry the exact same send that failed — `handleResend`
   * when a number is already staged (the OTP step is on screen and its own
   * resend link is locked behind a 60s countdown), `handleSendCode` when the
   * user is still on the phone-entry step.
   */
  const handleDeliveryRetry = async () => {
    // The lock is what makes the countdown honest. Without it the button fires
    // straight back into the budget that just refused us and the user learns
    // nothing except that the app is broken.
    if (retryCooldown > 0) return
    if (otpRequestInFlight.current) return
    if (pendingPhone) {
      // This control owns its error UI. OtpInput needs the rejection to keep
      // its digits/timer, while this banner must not leak an unhandled promise.
      await handleResend()
        .then(() => {
          setOtpChallengeState('ready')
          setExternalResendVersion((version) => version + 1)
        })
        .catch(() => {})
      return
    }
    await handleSendCode()
  }

  /**
   * Escape hatch out of the dead end: drop the staged number and go back to
   * the phone-entry step so the user can try a different line instead of
   * waiting on a carrier that is not delivering.
   */
  const handleDeliveryChangeNumber = () => {
    impact(ImpactStyle.Light)
    setDeliveryIssue(null)
    setRetryCooldown(0)
    setError('')
    setPhoneError('')
    setChangeTarget(null)
    setPendingPhone(null)
    setOtpChallengeState('idle')
    setInputPhone(pendingPhone ?? inputPhone)
    hasSent.current = false
  }

  /** Never trap a user who already owns email on a dead SMS path. */
  const emailRecoveryCta: PhoneOtpEmailCta = (() => {
    if (hasVerifiedAuthEmail(user, null)) return 'continue_app'
    if (realContactEmail(user, null) && !user?.email_confirmed_at) return 'verify_email'
    if (realContactEmail(user, null)) return 'continue_app'
    return 'sign_in_email'
  })()

  const handleEmailRecovery = useCallback(() => {
    impact(ImpactStyle.Light)
    productTelemetryService.recordEvent('form_submit', 'auth.phone_otp_recovery_email', {
      routePath: '/verify-phone',
      screenName: 'verify-phone',
      elementId: 'verify-phone.recovery-email',
      elementType: 'button',
      metadata: { cta: emailRecoveryCta },
    })
    if (emailRecoveryCta === 'continue_app') {
      setCompletingSignup(true)
      setPendingAuthPhone(null)
      router.replace('/(tabs)/explore' as never)
      return
    }
    if (emailRecoveryCta === 'verify_email') {
      router.replace('/(auth)/verify-email' as never)
      return
    }
    router.replace('/(auth)/login' as never)
  }, [emailRecoveryCta, router, setCompletingSignup, setPendingAuthPhone])

  // This screen links a verified phone to an already-authenticated email or
  // OAuth account through request-phone-link. There is no channel picker here:
  // the secure edge function sends the phone-change OTP over WhatsApp through
  // Verifik (send-phone-otp) after checking phone ownership.
  const handleUnavailableExit = async () => {
    // Misma razón que arriba: por la mutación, para que el SIGNED_OUT quede
    // marcado como intencional. Con la llamada directa, salir de acá también
    // terminaba en "Tu sesión expiró", que es falso — la cerró la persona.
    await signOut().catch((err) => {
      captureError(err, { screen: 'verify-phone', action: 'unavailable-signout' })
    })
    router.replace('/(auth)/login' as never)
  }

  if (!env.isPhoneAuthEnabled) {
    return (
      <AuthScaffold
        brandHeadline={t('welcome.headline')}
        brandSubtitle={t('welcome.subheadline')}
        background={colors.background}
      >
        <BackButton />
        <AuthHeader
          title={t('auth.phone_auth_unavailable_title')}
          subtitle={t('auth.phone_auth_unavailable_desc')}
          subtitleGap="action"
        />
        <AuthGradientButton
          title={t('auth.sign_in_button')}
          onPress={handleUnavailableExit}
          testID="verify-phone.unavailable-continue-button"
          entering={
            reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 4)
          }
        />
      </AuthScaffold>
    )
  }

  // One field and one button end their block just past the halfway line, and
  // pinning the footer to the bottom edge left ~45% of the phone as a single
  // empty band between the CTA and the login link. The stacked layout hands
  // this body the whole viewport, so grow into it and centre the block under
  // the pinned back button — the same treatment the sibling verify-email
  // screen uses. The split layout centres its own column already.
  const bodyStyle = isSplit ? undefined : { flexGrow: 1, justifyContent: 'center' as const }
  const otpChallengeReady = Boolean(pendingPhone && otpChallengeState === 'ready')
  const otpChallengeSending = Boolean(pendingPhone && otpChallengeState === 'sending')
  const otpChallengeFailed = Boolean(pendingPhone && otpChallengeState === 'failed')

  const formContent = (
    <>
      {/* ─── Navigation ─── */}
      <BackButton />

      <View style={bodyStyle}>
        {/* ─── Header ─── */}
        <AuthHeader
          title={
            phoneInUse
              ? t('auth.errors.phone_in_use_title')
              : otpChallengeSending
                ? t('auth.verify_phone_sending_title')
                : otpChallengeFailed
                  ? deliveryTitle(deliveryIssue)
                  : pendingPhone
                    ? t('auth.verify_phone_title')
                    : t('auth.link_phone_title')
          }
          subtitle={
            phoneInUse
              ? t('auth.errors.phone_in_use_body')
              : otpChallengeSending
                ? t('auth.verify_phone_sending_desc', { phone: pendingPhone })
                : otpChallengeFailed
                  ? t('auth.verify_phone_sms_unavailable_send_body')
                  : pendingPhone
                    ? changeTarget && pendingPhone === confirmedPhone
                      ? t('auth.verify_current_phone_desc', { phone: pendingPhone })
                      : t('auth.verify_phone_desc', { phone: pendingPhone })
                    : t('auth.link_phone_desc')
          }
        />

        {phoneInUse ? null : otpChallengeReady ? (
          <>
            <AuthOtpStep
              key={`${pendingPhone}:${externalResendVersion}`}
              onComplete={handleComplete}
              onResend={handleResend}
              disabled={verifyPhoneChangePending || sendPhoneVerificationPending}
              error={error}
              testID="verify-phone.otp-input"
            />
            {/* Correcting the number cannot depend on the carrier reporting a
                problem. It used to live only inside the `deliveryIssue` block,
                which is set when SMS delivery FAILS — and the case that traps
                people is the one where it succeeds: a typo that reaches a real
                stranger's handset, or a number the person no longer carries.
                Delivery is reported fine, no banner ever appears, and the OTP
                step is the only thing on screen.

                An account on this platform exists only once a phone is verified,
                so that is not a stuck screen, it is an account that can never be
                used and cannot be abandoned either — the same address cannot
                sign up again. One mistyped digit was permanent. */}
            <Button
              title={t('auth.verify_phone_sms_unavailable_change_number')}
              variant="text"
              onPress={handleDeliveryChangeNumber}
              disabled={verifyPhoneChangePending || sendPhoneVerificationPending}
              testID="verify-phone.change-number-button"
            />
          </>
        ) : otpChallengeSending ? (
          <View style={{ marginTop: SPACING.sm }} testID="verify-phone.sending-code">
            <AuthGradientButton
              title={t('auth.verify_phone_sending_button')}
              onPress={() => undefined}
              loading
              disabled
              testID="verify-phone.sending-code-button"
            />
          </View>
        ) : pendingPhone ? (
          deliveryIssue ? null : (
            <View
              style={{ gap: SPACING.sm, marginTop: SPACING.md }}
              testID="verify-phone.send-failed"
            >
              <AuthGradientButton
                title={t('auth.verify_phone_sms_unavailable_retry')}
                onPress={() => {
                  void handleDeliveryRetry().catch(() => {})
                }}
                loading={sendPhoneVerificationPending}
                disabled={verifyPhoneChangePending || sendPhoneVerificationPending}
                testID="verify-phone.send-failed-retry"
              />
              <Button
                title={t('auth.verify_phone_sms_unavailable_change_number')}
                variant="text"
                onPress={handleDeliveryChangeNumber}
                disabled={verifyPhoneChangePending || sendPhoneVerificationPending}
                testID="verify-phone.send-failed-change-number"
              />
            </View>
          )
        ) : (
          <AuthSendCodeStep
            buttonTitle={t('auth.send_code')}
            onSend={handleSendCode}
            loading={sendPhoneVerificationPending}
            disabled={!phoneValid}
            testID="verify-phone.send-button"
          >
            <PhoneInput
              label={t('auth.phone')}
              value={inputPhone}
              onChange={(value) => {
                setInputPhone(value)
                setError('')
                setPhoneError('')
              }}
              onValidityChange={handlePhoneValidityChange}
              error={phoneError || undefined}
              helperText={phoneHint ?? undefined}
              testID="verify-phone.phone-input"
            />
          </AuthSendCodeStep>
        )}

        {deliveryIssue ? (
          <PhoneOtpDeliveryRecovery
            reason={deliveryIssue.reason}
            mode={deliveryIssue.mode}
            emailCta={emailRecoveryCta}
            onEmailCta={handleEmailRecovery}
            onRetry={handleDeliveryRetry}
            retryLoading={sendPhoneVerificationPending}
            retryDisabled={retryCooldown > 0 || verifyPhoneChangePending}
            retrySeconds={retryCooldown}
            onChangeNumber={handleDeliveryChangeNumber}
            onSupport={handleDeliverySupport}
            testID="verify-phone.delivery-recovery"
          />
        ) : null}

        {/* Phone already linked to another account — clear, calm recovery. */}
        {phoneInUse ? (
          <View
            style={{ gap: SPACING.sm, marginTop: SPACING.lg }}
            testID="verify-phone.phone-in-use-card"
          >
            <AuthGradientButton
              title={t('auth.errors.phone_in_use_enter')}
              onPress={() => void handleEnterWithLinkedPhone()}
              loading={switchingAccount}
              testID="verify-phone.phone-in-use-enter"
            />
            <Button
              title={t('auth.errors.phone_in_use_other')}
              variant="text"
              onPress={handleUseOtherNumber}
              disabled={switchingAccount}
              testID="verify-phone.phone-in-use-other"
            />
          </View>
        ) : null}

        {/* Only the send step needs the banner. Once the OTP step is mounted the
            same `error` is already the field's error — the boxes shake, turn
            coral and print the message under themselves — so rendering the
            banner too printed one failure twice, once as coral text and once as
            a solid coral slab below it. */}
        <AuthErrorBanner error={otpChallengeReady || phoneInUse ? null : error || null} marginTop />

        {/* THE EXIT THAT ALREADY EXISTED, SAID OUT LOUD.
            A person with a verified email was never held by this screen: AuthGate admits any
            verified contact, so killing the app and reopening always walked straight in. But
            the screen presented itself as a toll — no visible way out — so a real user
            discovered the bypass by accident and reported it as a security hole. A gate that
            is optional must say so, or its honesty is decided by whoever force-quits first.
            Rendered ONLY when a verified email exists: for an account with no verified contact
            at all, this screen is the real gate and offers no exit. Reuses the same handler as
            the delivery-failure recovery, so the two exits cannot drift. The phone is still
            required to pay — the checkout names it, server-side, where no client can skip it. */}
        {emailRecoveryCta === 'continue_app' && !phoneInUse ? (
          <Button
            title={t('auth.verify_phone_later')}
            variant="text"
            onPress={handleEmailRecovery}
            testID="verify-phone.verify-later"
            accessibilityLabel={t('auth.verify_phone_later_a11y')}
          />
        ) : null}

        {/* Entry rule: either verified phone OR verified email (see
            account-contact.ts). Phone remains required before booking.
            There is no "do this later" exit here: AuthGate holds until a
            verified contact exists. Country picker +503 limits are a separate
            product fix. */}
      </View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
    >
      {formContent}
    </AuthScaffold>
  )
}
